// =============================================================================
// Session reference-data cache.
//
// In-memory store for the preloaded searchable dropdowns (Territory / Sales Rep
// / Customer). Each scoped list is fetched ONCE, cached here, and reused for the
// rest of the session — no per-keystroke network calls. Lives until logout:
// clearAuth() in auth.js calls clear() so a new sign-in starts cold.
//
// Module-level state survives route changes but NOT a full page reload — that's
// fine, prefetchForSession() re-warms the common lists on boot.
//
// Kept dependency-free (reads sessionStorage directly rather than importing
// auth.js) so auth.js can import clear() without a circular dependency.
// =============================================================================

import { resolveDataPath } from "./fieldHelpers";
import sessionStorage from "./sessionStore";

const API_URL = process.env.REACT_APP_API_URL;

// A generous cap for "the whole scoped list". Scoped territory/rep/customer
// sets are well under this; the unbounded admin-customer case never uses the
// cache (it falls back to live search — see buildCustomerConfig callers).
const BIG_TOP = 2000;

// key -> { data: Array | null, promise: Promise | null }
const store = new Map();
// key -> Set<callback>
const listeners = new Map();

function emit(key) {
  const subs = listeners.get(key);
  if (subs) subs.forEach((cb) => { try { cb(); } catch { /* ignore */ } });
}

// Synchronous read — returns the cached array, or undefined if not loaded yet.
export function peek(key) {
  const entry = store.get(key);
  return entry && Array.isArray(entry.data) ? entry.data : undefined;
}

export function has(key) {
  return peek(key) !== undefined;
}

// Subscribe to "this key's data changed" (load finished, or cache cleared).
// Returns an unsubscribe fn.
export function subscribe(key, cb) {
  let subs = listeners.get(key);
  if (!subs) { subs = new Set(); listeners.set(key, subs); }
  subs.add(cb);
  return () => { subs.delete(cb); };
}

// Returns a Promise<Array>. Dedupes concurrent loads and caches the resolved
// array. A failed load is NOT cached, so a transient error can be retried on
// the next open.
export function load(key, fetcher) {
  const entry = store.get(key);
  if (entry && Array.isArray(entry.data)) return Promise.resolve(entry.data);
  if (entry && entry.promise) return entry.promise;

  const promise = Promise.resolve()
    .then(() => fetcher())
    .then((data) => {
      const arr = Array.isArray(data) ? data : [];
      // An EMPTY list is answered but not kept (Issue 6 / DEFECT-40): the API
      // answers an empty rep list when a page of its Epicor walk was lost, and a
      // kept empty list would show "Territory D57 has no sales reps assigned"
      // for the rest of the session. A genuinely empty list is cheap to re-ask.
      if (arr.length === 0) {
        store.delete(key);
      } else {
        store.set(key, { data: arr, promise: null });
      }
      emit(key);
      return arr;
    })
    .catch((err) => {
      store.delete(key); // allow retry
      emit(key);
      throw err;
    });

  store.set(key, { data: null, promise });
  return promise;
}

// Wipe everything. Notifies open dropdowns so they drop stale lists. Called
// from clearAuth() on logout / 401.
export function clear() {
  const keys = Array.from(store.keys());
  store.clear();
  keys.forEach((key) => emit(key));
}

// -----------------------------------------------------------------------------
// Authed fetch — mirrors the manual Bearer header used elsewhere. The global
// interceptor in auth.js would inject this anyway, but being explicit keeps the
// loaders self-contained and consistent with AutocompleteField.
// -----------------------------------------------------------------------------
function authedFetchJson(url) {
  const headers = {};
  const token = sessionStorage.getItem("authToken");
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { headers }).then((r) =>
    r.ok ? r.json() : Promise.reject(r.status)
  );
}

// D99 is the admin/HQ "see everything" territory — it never narrows the rep
// list, so we treat it as "no scope".
export const ADMIN_TERRITORY = "D99";
export function isAdminTerritory(territoryId) {
  return (territoryId || "").toUpperCase() === ADMIN_TERRITORY;
}

// -----------------------------------------------------------------------------
// Dataset descriptors — { key, fetcher }. The key encodes dataset + scope so
// the cache narrows correctly (e.g. reps differ per territory). Both the
// dropdown configs and prefetchForSession() build descriptors from these so
// keys always line up.
// -----------------------------------------------------------------------------
export function territoriesDescriptor() {
  return {
    key: "territories",
    fetcher: () =>
      authedFetchJson(`${API_URL}/api/Territory/info?Top=${BIG_TOP}`).then(
        (d) => resolveDataPath(d, "territories") || []
      ),
  };
}

export function salesRepsDescriptor(territoryId) {
  const scoped = territoryId && !isAdminTerritory(territoryId) ? territoryId : "";
  const params = scoped ? `territoryId=${encodeURIComponent(scoped)}&` : "";
  return {
    key: `salesReps:${scoped || "all"}`,
    fetcher: () =>
      authedFetchJson(`${API_URL}/api/SalesRep?${params}Top=${BIG_TOP}`).then(
        (d) => resolveDataPath(d, "salesReps") || []
      ),
  };
}

// Active product systems (parts) — global, not scoped to the user. The
// /api/Part/systems endpoint returns the full active-system list (the search
// endpoint caps at 50), so it's the right source for a preloaded dropdown.
export function systemsDescriptor() {
  return {
    key: "systems",
    fetcher: () =>
      authedFetchJson(`${API_URL}/api/Part/systems`).then(
        (d) => resolveDataPath(d, "systems") || []
      ),
  };
}

export function customersDescriptor(salesRepCode) {
  return {
    key: `customers:${salesRepCode}`,
    fetcher: () =>
      authedFetchJson(
        `${API_URL}/api/Customer?salesRepCode=${encodeURIComponent(salesRepCode)}&top=${BIG_TOP}&slim=true`
      ).then((d) => resolveDataPath(d, "customers") || []),
  };
}

// Customers in ONE territory — the server resolves the territory to its rep
// codes (via SalesTRPs) and answers from the load-all cache. Used when a
// territory is picked in a filter panel but no rep is. 25k top rather than
// BIG_TOP: a top-level distributor territory can exceed 2k customers.
export function territoryCustomersDescriptor(territoryId) {
  return {
    key: `customers:territory:${territoryId}`,
    fetcher: () =>
      authedFetchJson(
        `${API_URL}/api/Customer?territoryId=${encodeURIComponent(territoryId)}&top=25000&slim=true`
      ).then((d) => resolveDataPath(d, "customers") || []),
  };
}

// The whole customer base VISIBLE TO THIS USER — the load-all endpoint is
// JWT-scoped server-side, so one call returns the full ~5.7k base for a D99
// admin/distributor and the territory subtree for everyone else, straight
// from the API's warmed load-all cache (one in-memory slice, not an Epicor
// scan). Powers the customer type-ahead client-side, which used to fire a
// live Epicor query per keystroke batch (1.4-9s each on the pilot tenant
// depending on load). The 25k top comfortably clears the server's 20k
// fetch ceiling.
export function allCustomersDescriptor() {
  return {
    key: "customers:all",
    // slim=true trims each row to the dropdown fields (identity/city/rep/
    // terms/status) — the full-DTO version of this 11k-row payload measured
    // ~13s end-to-end; slim serves the same rows at a fraction of the bytes.
    fetcher: () =>
      authedFetchJson(`${API_URL}/api/Customer?top=25000&slim=true`).then(
        (d) => resolveDataPath(d, "customers") || []
      ),
  };
}

// Convenience: kick a descriptor's load without caring about the result here
// (errors are swallowed — best-effort background warm).
export function warm(descriptor) {
  if (!descriptor) return;
  load(descriptor.key, descriptor.fetcher).catch(() => { /* best-effort */ });
}

// -----------------------------------------------------------------------------
// Background prefetch — called right after login and on app boot when already
// authenticated, so these load in parallel with the dashboard's own calls.
// Warms every list the Territory / Sales Rep / Customer dropdowns read.
// Reads scope straight from sessionStorage.
// -----------------------------------------------------------------------------
export function prefetchForSession() {
  if (!sessionStorage.getItem("authToken")) return;

  warm(territoriesDescriptor());

  const territoryId = sessionStorage.getItem("territoryId") || "";
  warm(salesRepsDescriptor(territoryId));
  // The UNSCOPED rep list backs the rep pickers built with no territory (the
  // Customer page's filter/step pickers). Same cache key as the warm above
  // for D99 users (admin territory ⇒ unscoped), so the extra call only
  // happens for territory-scoped users.
  warm(salesRepsDescriptor(""));

  // Customer list — the biggest dataset and the one the list pages' Customer
  // dropdowns read when nothing narrows them. JWT-scoped server-side, so this
  // one warm loads the right list for every role (full base for D99
  // admins/distributors, territory subtree for everyone else).
  warm(allCustomersDescriptor());

  // A rep's own (narrower) customer list — used once their rep code is picked
  // or defaulted into a filter. Small and bounded.
  const salesRepCode = sessionStorage.getItem("salesRepCode") || "";
  if (salesRepCode) warm(customersDescriptor(salesRepCode));
}
